'use client';

import type { PersistedHandle } from '@bomdisoft/store/persisted/types';
import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useSyncExternalStore } from 'react';

/**
 * The same hook, with the address the Next router reports.
 *
 * Reading `useSearchParams` does two things the plain React entry point cannot:
 * the server snapshot carries the value a link was opened with, so a shared
 * address renders correctly before hydration, and a soft navigation that
 * changes a parameter re-renders every consumer of that handle.
 *
 * Only this entry point touches `useSearchParams`, so a provider that seeds a
 * reducer can call `handle.read()` instead and never force a Suspense boundary
 * on its whole tree.
 */
export const usePersisted = <T>(handle: PersistedHandle<T>, scope?: string): [T, (next: T | ((current: T) => T)) => void] => {
  const searchParams = useSearchParams();
  const raw = searchParams?.get(handle.param) ?? null;

  const subscribe = useCallback((listener: () => void) => handle.subscribe(listener, scope), [handle, scope]);
  const snapshot = useCallback(() => handle.read(scope), [handle, scope]);
  const serverSnapshot = useCallback(() => handle.fromUrl(raw, scope), [handle, raw, scope]);
  const value = useSyncExternalStore(subscribe, snapshot, serverSnapshot);

  useEffect(() => {
    handle.reflect(value, scope);
  }, [handle, scope, value]);

  const write = useCallback((next: T | ((current: T) => T)) => handle.write(next, scope), [handle, scope]);
  return [value, write];
};
